// Theme and document language. "system" follows prefers-color-scheme; light/dark are manual.
import { useEffect } from 'react';
import type { UiLang } from '@valyrio/engine/types';
import type { Theme } from '../state/store';

/** Both themes' page ground, read from the tokens (tokens.css --page-light / --page-dark); empty
 *  until the stylesheet is in (the metas then keep index.html's values). */
function pageGround(root: HTMLElement): Record<'light' | 'dark', string> {
  const css = getComputedStyle(root);
  return { light: css.getPropertyValue('--page-light').trim(), dark: css.getPropertyValue('--page-dark').trim() };
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme);
  else root.removeAttribute('data-theme');
  // Status bar / browser chrome colour follows the page ground.
  const page = pageGround(root);
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  metas.forEach((m) => {
    const media = m.getAttribute('media') ?? '';
    const scheme = media.includes('dark') ? 'dark' : 'light';
    const colour = page[theme === 'system' ? scheme : theme];
    if (colour) m.setAttribute('content', colour);
  });
  // iOS status bar style for the next launch of the Home Screen app (see public/prepaint.js).
  if (root.classList.contains('is-ios')) {
    const dark = theme === 'dark' || (theme === 'system' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')?.setAttribute('content', dark ? 'black-translucent' : 'default');
  }
}

export function useDocumentPrefs(theme: Theme, lang: UiLang): void {
  useEffect(() => applyTheme(theme), [theme]);
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);
}

export const THEME_ORDER: readonly Theme[] = ['system', 'light', 'dark'];
